<template>
  <div ref="containerRef" class="flex flex-col space-y-16">
    <div class="grid gap-16 md:flex md:flex-wrap">
      <AddressInputText
        v-if="fields.lastName"
        v-model="lastName"
        :error="errors.lastName ?? undefined"
        field-name="lastName"
        :label="i18n(addressFieldsTranslations.lastName)"
      />
      <AddressInputText
        v-if="fields.firstName"
        v-model="firstName"
        :error="errors.firstName ?? undefined"
        field-name="firstName"
        :label="i18n(addressFieldsTranslations.firstName)"
      />
    </div>

    <div class="grid gap-16 md:flex md:flex-wrap">
      <AddressInputText
        v-model="lastNamePronunciation"
        :error="errors.lastNamePronunciation ?? undefined"
        field-name="lastNamePronunciation"
        :label="i18n(addressFieldsTranslations.lastNamePronunciation)"
      />
      <AddressInputText
        v-model="firstNamePronunciation"
        :error="errors.firstNamePronunciation ?? undefined"
        field-name="firstNamePronunciation"
        :label="i18n(addressFieldsTranslations.firstNamePronunciation)"
      />
    </div>

    <AddressInputSelect
      v-if="fields.country"
      v-model="country"
      :error="errors.country ?? undefined"
      field-name="country"
      :label="i18n(addressFieldsTranslations.country)"
      :options="countryOptions"
    />

    <div class="grid gap-16 md:flex md:flex-wrap">
      <AddressAutocomplete
        v-if="fields.postalCode"
        v-model="searchQuery"
        class="md:min-w-[20rem] md:flex-1"
        :country
        :error="errors.postalCode || undefined"
        :feature-code
        field-name="postalCode"
        :label="i18n(addressFieldsTranslations.postalCode)"
        @select-item="handleSearchSelectItem"
      />

      <div class="md:min-w-[20rem] md:flex-1">
        <AddressInputSelect
          v-model="stateOrProvince"
          :error="errors.stateOrProvince ?? undefined"
          field-name="stateOrProvince"
          :label="i18n(addressFieldsTranslations.stateOrProvince)"
          :options="states"
        />
      </div>
    </div>

    <AddressInputText
      v-if="fields.city"
      v-model="city"
      :error="errors.city || undefined"
      field-name="city"
      :label="i18n(addressFieldsTranslations.city)"
    />

    <AddressInputText
      v-if="fields.street"
      v-model="street"
      :error="errors.street ?? undefined"
      field-name="street"
      :label="i18n(addressFieldsTranslations.street)"
    />

    <AddressOptionalField
      v-if="fields.street2"
      button-id="show-street2"
      :displayed="shouldDisplayOptionalField('street2')"
      :label="i18n(addressFieldsTranslations.street2Hidden)"
      :value="street2"
    >
      <AddressInputText
        v-model="street2"
        :error="errors.street2 || undefined"
        field-name="street2"
        :label="i18n(addressFieldsTranslations.street2)"
      />
    </AddressOptionalField>

    <AddressOptionalField
      v-if="fields.company"
      button-id="show-company"
      :displayed="shouldDisplayOptionalField('company')"
      :label="i18n(addressFieldsTranslations.companyNameHidden)"
      :value="company"
    >
      <AddressInputText
        v-model="company"
        :error="errors.company ?? undefined"
        field-name="company"
        :label="companyFieldLabel"
      />
    </AddressOptionalField>

    <AddressInputText
      v-if="fields.publicCompanyName"
      v-model="publicCompanyName"
      :description="i18n(addressFieldsTranslations.publicCompanyNameInfoText)"
      :error="errors.publicCompanyName ?? undefined"
      field-name="publicCompanyName"
      :label="i18n(addressFieldsTranslations.publicCompanyName)"
    />

    <AddressInputPhone
      v-if="fields.phone"
      v-model="phone"
      :default-country="defaultPhoneCode"
      :description="phoneHelpMessage"
      :error="errors.phone ?? undefined"
      field-name="phone"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { Country } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import type { Option } from '@ds/components/InputSelect'

import addressFieldsTranslations from '../../../translations/addressFields.translations'
import type { Address } from '../../../types/address'
import type {
  AddressFieldsProps,
  Errors,
  OptionalFields,
} from '../../../types/addressFields'
import type { BmPlacesDetails } from '../../../types/bmPlaces'
import { getAllStates } from '../../../utils/state/getAllStates'
import { getShippingStates } from '../../../utils/state/getShippingStates'
import AddressAutocomplete from '../../AddressAutocomplete/AddressAutocomplete.vue'
import AddressInputPhone from '../AddressInputPhone/AddressInputPhone.vue'
import AddressInputSelect from '../AddressInputSelect/AddressInputSelect.vue'
import AddressInputText from '../AddressInputText/AddressInputText.vue'
import AddressOptionalField from '../AddressOptionalField/AddressOptionalField.vue'

const props = withDefaults(defineProps<AddressFieldsProps>(), {
  companyFieldLabelMessage: '',
  countries: () => [],
  defaultCountry: Country.JP,
  phone: undefined,
  phoneHelpMessage: '',
  shippingState: false,
  optionalFields: () => ['company', 'street2'],
  autocompleteMaxItems: 5,
  autoScrollOnError: true,
  inputAutocompleteAriaDescribedbyId: undefined,
})

const firstName = defineModel<NonNullable<Address['firstName']>>('firstName')
const lastName = defineModel<NonNullable<Address['lastName']>>('lastName')
const firstNamePronunciation = defineModel<
  NonNullable<Address['firstNamePronunciation']>
>('firstNamePronunciation')
const lastNamePronunciation = defineModel<
  NonNullable<Address['lastNamePronunciation']>
>('lastNamePronunciation')
const company = defineModel<NonNullable<Address['company']>>('company')
const publicCompanyName = defineModel<string>('publicCompanyName')
const country = defineModel<NonNullable<Address['country']>>('country', {
  default: null,
})
const stateOrProvince = defineModel<NonNullable<Address['stateOrProvince']>>(
  'stateOrProvince',
  { default: null },
)
const street = defineModel<NonNullable<Address['street']>>('street')
const street2 = defineModel<NonNullable<Address['street2']>>('street2')
const postalCode = defineModel<NonNullable<Address['postalCode']>>('postalCode')
const city = defineModel<NonNullable<Address['city']>>('city')
const phone = defineModel<NonNullable<Address['phone']>>('phone')

const emit = defineEmits(['autocompleted'])

const i18n = useI18n()

const searchQuery = computed<string>({
  get() {
    return postalCode.value ?? ''
  },
  set(value: string) {
    postalCode.value = value
  },
})

const containerRef = ref<HTMLElement>()

const fields = computed(() => ({
  city: city.value !== undefined,
  company: company.value !== undefined,
  country: props.country !== undefined,
  firstName: firstName.value !== undefined,
  firstNamePronunciation: firstNamePronunciation.value !== undefined,
  lastName: lastName.value !== undefined,
  lastNamePronunciation: firstNamePronunciation.value !== undefined,
  phone: props.phone !== undefined,
  postalCode: postalCode.value !== undefined,
  publicCompanyName: publicCompanyName.value !== undefined,
  street: street.value !== undefined,
  street2: street2.value !== undefined,
}))

const companyFieldLabel = computed(() =>
  isEmpty(props.companyFieldLabelMessage)
    ? i18n(addressFieldsTranslations.companyOptional)
    : props.companyFieldLabelMessage,
)

const defaultPhoneCode = computed(() => {
  if (isEmpty(country.value)) {
    return props.defaultCountry ?? Country.JP
  }

  return country.value
})

const countryOptions = computed((): Option[] => {
  if (!isEmpty(props.countries)) return props.countries

  const translatedCountries = Object.values(Country).map((_country) => ({
    label: i18n.country(_country),
    value: _country,
  }))

  return translatedCountries.sort((a, b) => a.label.localeCompare(b.label))
})

const states = computed(() => {
  const getStates = props.shippingState ? getShippingStates : getAllStates

  return isEmpty(country.value) ? [] : getStates(country.value)
})

const shouldDisplayOptionalField = (fieldName: OptionalFields) => {
  return !props.optionalFields.includes(fieldName)
}

watch(
  () => props.errors,
  (errors, oldErrors) => {
    if (!containerRef.value || isEmpty(errors) || !props.autoScrollOnError) {
      return
    }

    if (isEmpty(removeEmptyValuesInObject(errors))) {
      return
    }

    // Don't scroll if content hasn't actually changed
    if (isEqual(errors, oldErrors)) {
      return
    }

    const firstErrorField = (Object.keys(errors) as (keyof Errors)[]).find(
      (key) => errors[key],
    )
    if (firstErrorField) {
      containerRef.value?.querySelector(`#${firstErrorField}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  },
)

function handleSearchSelectItem(address: BmPlacesDetails, index: number) {
  const currentPostalCode = address.postalCode || ''
  const state =
    Object.values(states.value).find(
      ({ label }) => label === address.stateProvince,
    )?.value || ''

  stateOrProvince.value = state
  searchQuery.value = address.street || ''

  if (currentPostalCode.length > 3) {
    searchQuery.value = `${currentPostalCode.substring(0, 3)}-${currentPostalCode
      .replace('-', '')
      .substring(3)}`
  } else {
    searchQuery.value = address.postalCode || ''
  }

  city.value = address.city ?? undefined
  postalCode.value = address.postalCode ?? undefined
  country.value = address.country ?? undefined
  street.value = address.street ?? undefined
  street2.value = address.street2 ?? undefined

  emit('autocompleted', address, index)
}
</script>
